import React, { useEffect, useState } from 'react'
import { Autocomplete, Box, Chip, TextField } from '@mui/material'
import { AppBrandMark } from "@components"
import { dsColorNew } from "@palette"

/**
 * Multi-select picker for Price Seeker providers (rental brands).
 *
 * Pulls from /api/my-business/catalog/providers — only the providers
 * with at least one quote in this tenant's RESULTS_CH are listed, so
 * the picker doesn't show 5000 dead brands. The operator's own brand
 * is flagged so it visually stands out at the top.
 *
 *   <ProviderSlicer value={[51, 163]} onChange={(ids) => ...}/>
 */
export default function ProviderSlicer({ value = [], onChange, label = 'Providers' }) {
	const [options, setOptions] = useState([])
	useEffect(() => {
		fetch('/api/my-business/catalog/providers', { credentials: 'include' })
			.then(r => r.ok ? r.json() : [])
			.then((rows) => {
				// Own provider first, then by volume.
				const sorted = (rows || []).slice().sort((a, b) => {
					if (a.is_own !== b.is_own) return a.is_own ? -1 : 1
					return (b.rows || 0) - (a.rows || 0)
				})
				setOptions(sorted)
			})
			.catch(() => setOptions([]))
	}, [])

	const selected = options.filter(o => value.includes(o.id))

	return (
		<Autocomplete multiple size="small" sx={{ minWidth: 260 }}
		              options={options}
		              value={selected}
		              getOptionLabel={(o) => o.name}
		              isOptionEqualToValue={(a, b) => a.id === b.id}
		              onChange={(_, v) => onChange(v.map(x => x.id))}
		              renderOption={(props, o) => (
			              <Box component="li" {...props} key={o.id}>
				              <Box display="flex" alignItems="center" gap={1} width="100%">
					              {o.is_own && (
						              <Chip size="small" label="YOU"
						                    sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}/>
					              )}
					              <AppBrandMark name={o.name}/>
					              <Box flex={1}>{o.name}</Box>
					              <Box sx={{ color: dsColorNew.textMuted, fontSize: 12 }}>
						              {o.rows >= 1000 ? `${Math.round(o.rows / 1000)}k` : o.rows}
					              </Box>
				              </Box>
			              </Box>
		              )}
		              renderTags={(items, getProps) => items.map((o, i) => (
			              <Chip {...getProps({ index: i })} size="small" key={o.id}
			                    avatar={<AppBrandMark name={o.name}/>}
			                    label={o.name}
			                    color={o.is_own ? 'primary' : 'default'}/>
		              ))}
		              renderInput={(params) => (
			              <TextField {...params} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} label={label} placeholder={selected.length ? '' : 'All'}/>
		              )}/>
	)
}
